<script lang="ts">
  /**
   * The three states in which the table has nothing to draw (spec 6.2). Which one the page is in
   * is decided by `emptyStateVariant` in the view model; this component only draws it.
   *
   * While the rows are still loading (#2265) `empty` is only true of the rows so far, so it draws
   * a provisional sentence instead of the run's "found nothing" verdict.
   */
  import { formatNumberWithCommas } from "$lib/utils/number";

  import {
    exposureFilterLabel,
    providerLabel,
    provisionalSuffix,
    type EmptyStateVariant,
    type ExposureFilter,
    type Provider,
  } from "../exposedSensitiveDocuments";
  import { STATUS_LABELS, type RowStatus } from "./status";

  let {
    variant,
    exposureFilter = null,
    providerFilter = null,
    totalRows = 0,
    provisional = false,
    infoTypesFiltered = false,
    statusFilter = [],
    onShowAll,
  }: {
    variant: EmptyStateVariant;
    exposureFilter?: ExposureFilter | null;
    providerFilter?: Provider | null;
    /** Rows that exist before the filters, so `filtered-empty` can say what is being hidden. */
    totalRows?: number;
    /** The rows are still loading, so `totalRows` and `empty` are only the rows so far (#2265). */
    provisional?: boolean;
    onShowAll?: () => void;
    /** True when the active info-type list is trimming, so the sentence can name it (#2206). */
    infoTypesFiltered?: boolean;
    /** The statuses picked; empty when none is, or when a failed read leaves the filter inert. */
    statusFilter?: readonly RowStatus[];
  } = $props();

  const activeFilters = $derived(
    [
      exposureFilter ? `exposure ${exposureFilterLabel(exposureFilter)}` : undefined,
      providerFilter ? `provider ${providerLabel(providerFilter)}` : undefined,
      infoTypesFiltered ? "the active category list" : undefined,
      statusFilter.length > 0
        ? `status ${statusFilter.map((status) => STATUS_LABELS[status]).join(" or ")}`
        : undefined,
    ]
      .filter((part): part is string => part !== undefined)
      .join(" and "),
  );
</script>

<div
  class="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 bg-white px-6 py-16 text-center"
  data-testid="exposed-documents-empty-state"
  data-variant={variant}
>
  {#if variant === "off"}
    <h3 class="font-bold text-lg">Sensitive Data Exposure has not been run for this organisation</h3>
    <p class="max-w-xl text-sm text-gray-600">
      This page is filled by two pipeline steps, <code class="font-mono">postprocess-exposure</code> and
      <code class="font-mono">publish-exposure</code>. No provider has published or been skipped for this organisation,
      so there are no exposed sensitive documents to be empty of. Once a run publishes, the rows and the per-provider
      freshness appear here.
    </p>
  {:else if variant === "empty" && provisional}
    <h3 class="font-bold text-lg">No exposed sensitive documents so far</h3>
    <p class="max-w-xl text-sm text-gray-600">
      None of the documents loaded so far match. The rest are still loading.
    </p>
  {:else if variant === "empty"}
    <h3 class="font-bold text-lg">No exposed sensitive documents</h3>
    <p class="max-w-xl text-sm text-gray-600">
      The last run published successfully and found nothing that is both sensitive and exposed - no document is public
      or shared with a principal outside the organisation.
    </p>
  {:else}
    <h3 class="font-bold text-lg">No exposed sensitive documents match these filters</h3>
    <p class="max-w-xl text-sm text-gray-600">
      {formatNumberWithCommas(totalRows)} exposed sensitive document{totalRows === 1 ? "" : "s"} exist for this organisation{provisionalSuffix(provisional)},
      but none match {activeFilters || "the current filters"}.
    </p>
    {#if onShowAll}
      <button class="rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-white" onclick={() => onShowAll?.()}>
        Show all
      </button>
    {/if}
  {/if}
</div>
